import { cn } from '@/lib/utils';
import { Slider } from '@chiroup/components/form/Slider';
import React, { use, useMemo } from 'react';
import { ThemeContext } from '../../../../../contexts/theme.context';

type Props = {
  rows?: any[];
  onChange: (
    tab: string,
    key: string,
    val: {
      value?: number;
      pain?: boolean;
      side?: 'Right' | 'Left' | 'Both' | 'SpinalArea';
    }
  ) => void;
  onChangeComplete?: (val: any) => void;
  currentTab: string;
  value: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          value?: number;
          pain?: boolean;
        };
      };
    };
  };
  side: 'Right' | 'Left' | 'Both' | 'SpinalArea';
  showSide?: boolean;
  romSliderStep: number;
  disabled?: boolean;
};

const ROM: React.FC<Props> = ({
  rows = [],
  onChange,
  onChangeComplete,
  currentTab,
  value,
  side,
  showSide,
  romSliderStep,
  disabled = false,
}) => {
  const { theme } = use(ThemeContext);

  const isAllPainful = useMemo(() => {
    if (!rows?.length || !value?.[currentTab]) return false;

    return rows.every(rom => {
      const romData = value[currentTab]?.[rom.value]?.[side];

      return romData?.pain === true;
    });
  }, [rows, value, currentTab, side]);

  return (
    <div className="flex flex-col gap-6 py-6 w-full min-w-0">
      {showSide && (
        <p className="text-2xl 2xl:text-lg text-center font-bold 2xl:font-semibold uppercase 2xl:normal-case tracking-wide 2xl:tracking-normal border-b-2 2xl:border-b-0 border-gray-300 dark:border-dark-gray-600 pb-2 2xl:pb-0">
          {side === 'Right' ? 'Right' : 'Left'}
        </p>
      )}
      <div className="flex flex-row gap-1 items-center text-gray-400 text-xs justify-end">
        All Painful?
        <div
          className={cn(
            'h-5 w-5 border-2 rounded-full cursor-pointer',
            isAllPainful
              ? 'bg-red-500 border-gray-900'
              : 'bg-gray-300 hover:bg-gray-300 border-gray-400 hover:border-gray-500 dark:bg-dark-gray-700 dark:hover:bg-dark-gray-600 dark:border-dark-gray-900 dark:hover:border-dark-gray-800'
          )}
          onClick={() => {
            const wasPainful = isAllPainful;

            if (!wasPainful) {
              if (onChangeComplete) {
                const currentTabData = value?.[currentTab] || {};
                const updatedTabData = { ...currentTabData };

                rows.forEach(rom => {
                  const currentRomData = updatedTabData[rom.value] || {};
                  updatedTabData[rom.value] = {
                    ...currentRomData,
                    [side]: {
                      ...currentRomData[side],
                      pain: true,
                      value: currentRomData[side]?.value,
                    },
                  };
                });

                const newValue = {
                  ...value,
                  [currentTab]: {
                    ...updatedTabData,
                    touched: true,
                  },
                };

                onChangeComplete(newValue);
              } else {
                rows.forEach(rom => {
                  const changeVal = {
                    pain: true,
                    value: value?.[currentTab]?.[rom.value]?.[side]?.value,
                    side: side,
                  };
                  onChange(currentTab, rom.value, changeVal);
                });
              }
            } else {
              if (onChangeComplete) {
                const currentTabData = value?.[currentTab] || {};
                const updatedTabData = { ...currentTabData };

                rows.forEach(rom => {
                  const currentRomData = updatedTabData[rom.value] || {};
                  updatedTabData[rom.value] = {
                    ...currentRomData,
                    [side]: {
                      ...currentRomData[side],
                      pain: false,
                      value: currentRomData[side]?.value,
                    },
                  };
                });

                const newValue = {
                  ...value,
                  [currentTab]: {
                    ...updatedTabData,
                    touched: true,
                  },
                };

                onChangeComplete(newValue);
              } else {
                rows.forEach(rom => {
                  const changeVal = {
                    pain: false,
                    value: value?.[currentTab]?.[rom.value]?.[side]?.value,
                    side: side,
                  };
                  onChange(currentTab, rom.value, changeVal);
                });
              }
            }
          }}
        />
      </div>
      {rows.map(rom => (
        <div key={rom.value} className="flex flex-col">
          <div className="flex flex-row items-center justify-between gap-2">
            <span className="text-md font-bold leading-7 text-gray-900 truncate sm:text-lg sm:tracking-tight dark:text-white">
              {rom.name}
            </span>
            <div className="flex flex-row gap-1 items-center text-gray-400 text-xs shrink-0">
              Pain?
              <div
                className={cn(
                  'h-5 w-5 border-2 rounded-full cursor-pointer',
                  value?.[currentTab]?.[rom.value]?.[side]?.pain
                    ? 'bg-red-500 border-gray-900'
                    : 'bg-gray-300 hover:bg-gray-300 border-gray-400 hover:border-gray-500 dark:bg-dark-gray-700 dark:hover:bg-dark-gray-600 dark:border-dark-gray-900 dark:hover:border-dark-gray-800'
                )}
                onClick={() => {
                  if (disabled) return;

                  onChange(currentTab, rom.value, {
                    pain: !value?.[currentTab]?.[rom.value]?.[side]?.pain,
                    value: value?.[currentTab]?.[rom.value]?.[side]?.value,
                    side: side,
                  });
                }}
              />
            </div>
          </div>
          {rom && (
            <Slider
              disabled={disabled}
              min={rom.min}
              max={rom.max}
              value={
                typeof value?.[currentTab]?.[rom.value]?.[side]?.value === 'number'
                  ? value[currentTab][rom.value][side].value
                  : rom.normal
              }
              pain={value?.[currentTab]?.[rom.value]?.[side]?.pain || false}
              left={`${rom.min}`}
              right={`${rom.max}`}
              step={romSliderStep}
              onChange={(val: { value?: number; pain?: boolean }) => {
                if (disabled) return;

                onChange(currentTab, rom.value, { ...val, side });
              }}
              preferred={rom.normal}
              valueSet={typeof value?.[currentTab]?.[rom.value]?.[side]?.value === 'number'}
              grayColor={theme === 'dark' ? '#101827' : '#e5e7eb'}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default ROM;
